import type { Instance } from "flatpickr/dist/types/instance";

import "flatpickr/dist/themes/light.css";
import { styles } from "@repo/helpers";
import { memo, useCallback, useEffect, useRef } from "react";
import Flatpickr from "react-flatpickr";

import type { InternalDatePickerMarkupProps } from "./types";

import { InputComponent } from "../input";
import style from "./style.module.scss";

export const DateTimePickerMarkup = memo((props: InternalDatePickerMarkupProps) => {
	const { placeholder, datePickerOptions, ...inputProps } = props;
	const { id, name, disabled, value, error, className } = inputProps;
	const { allowInput } = datePickerOptions;
	const { onChange, onFocus, onUnfocused, onReady } = props;
	const instanceRef = useRef<Instance>(null);

	const onFlatpickrChange = useCallback(
		(selectedDates: Date[], dateStr: string, instance: Instance) => {
			onChange(selectedDates, dateStr, instance);
		},
		[onChange],
	);

	const onFlatpickrReady = useCallback(
		(selectedDates: Date[], dateStr: string, instance: Instance) => {
			instanceRef.current = instance;
			if (onReady) onReady(selectedDates, dateStr, instance);
		},
		[onReady],
	);

	useEffect(() => {
		if (instanceRef.current) {
			if (disabled) {
				instanceRef.current._input.setAttribute("disabled", "disabled");
				instanceRef.current._input.classList.add(style.disabled);
				if (instanceRef.current.altInput) {
					instanceRef.current.altInput.setAttribute("disabled", "disabled");
					instanceRef.current.altInput.classList.add(style.disabled);
				}
			} else {
				instanceRef.current._input.removeAttribute("disabled");
				instanceRef.current._input.classList.remove(style.disabled);
				if (instanceRef.current.altInput) {
					instanceRef.current.altInput.removeAttribute("disabled");
					instanceRef.current.altInput.classList.remove(style.disabled);
				}
			}
		}
	}, [disabled]);

	useEffect(() => {
		if (instanceRef.current && (value === undefined || value === null || value === "")) {
			instanceRef.current.clear(false);
		}
	}, [value]);

	const clearValue = useCallback(() => {
		if (disabled) return;

		if (instanceRef.current) {
			instanceRef.current.clear();
		} else {
			onChange([], "", null);
		}
	}, [onChange, disabled]);

	return (
		<InputComponent
			label={props.label}
			clear={clearValue}
			error={error}
			includeError={props.includeError}
			validationError={props.validationError}
			validator={props.validator}
		>
			<Flatpickr
				onChange={onFlatpickrChange}
				onBlur={onUnfocused}
				onFocus={onFocus}
				onReady={onFlatpickrReady}
				id={id}
				name={name || id}
				disabled={disabled}
				value={value || ""}
				placeholder={placeholder ?? "M.D.YY or M.D.YYYY"}
				readOnly={!allowInput}
				options={datePickerOptions}
				className={styles(style.formControl, className, disabled && style.disabled, error && style.error)}
				inputMode="decimal"
			/>
		</InputComponent>
	);
});
